import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Share2, Sparkles, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== HELPERS ==============
const MONTH_SV = ['JANUARI','FEBRUARI','MARS','APRIL','MAJ','JUNI','JULI','AUGUSTI','SEPTEMBER','OKTOBER','NOVEMBER','DECEMBER'];

function ChallengeIcon({ emoji, size = 'lg', celebrating = false }) {
  const sizes = { sm: 'text-4xl', md: 'text-6xl', lg: 'text-8xl' };
  return (
    <div className="relative flex items-center justify-center">
      {celebrating && (
        <>
          {[...Array(12)].map((_, i) => {
            const angle = (i / 12) * Math.PI * 2;
            const r = 55 + (i % 3) * 8;
            return (
              <span
                key={i}
                className="absolute text-lg"
                style={{
                  left: `calc(50% + ${Math.cos(angle) * r}px)`,
                  top: `calc(50% + ${Math.sin(angle) * r}px)`,
                  transform: 'translate(-50%,-50%)',
                  animation: `confetti 2s ${i * 0.1}s infinite ease-out`,
                }}
              >
                {['✨','🎉','💫','⭐'][i % 4]}
              </span>
            );
          })}
        </>
      )}
      <div className={`${sizes[size]} drop-shadow-lg`} style={{ filter: 'drop-shadow(0 4px 16px rgba(255,255,255,0.3))' }}>
        {emoji}
      </div>
    </div>
  );
}

function CalendarPlate({ startDate, endDate, isActive, accent }) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const today = new Date();
  const showDate = isActive ? today : start;
  const day = String(showDate.getDate()).padStart(2, '0');
  const monthLabel = MONTH_SV[showDate.getMonth()];
  return (
    <div className="relative mx-auto" style={{ width: '200px' }}>
      <div
        className="rounded-t-xl py-2 text-center font-black tracking-widest text-black text-xs"
        style={{ background: accent }}
      >
        {monthLabel}
      </div>
      <div className="rounded-b-xl py-6 text-center bg-gradient-to-b from-[#222827] to-[#161b1a] border-2 border-t-0 border-[#2a3231]">
        <div className="text-white text-6xl font-black" style={{ fontFamily: '"Fredoka", sans-serif', textShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>{day}</div>
        <div className="text-[10px] text-white/50 mt-1 tracking-widest">
          {start.getDate()} — {end.getDate()} {MONTH_SV[end.getMonth()].slice(0,3).toLowerCase()}
        </div>
      </div>
    </div>
  );
}

function ProgressRing({ percent, size = 300, stroke = 3, color = '#4ADE80' }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (percent / 100) * c;
  return (
    <svg width={size} height={size} className="absolute inset-0 m-auto pointer-events-none" style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      <circle
        cx={size/2} cy={size/2} r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={c}
        strokeDashoffset={off}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(0.34, 1.56, 0.64, 1)', filter: `drop-shadow(0 0 8px ${color})` }}
      />
    </svg>
  );
}

// ============== DETAIL MODAL ==============
function ChallengeDetailModal({ challenge, onClose, onUpdate, onDeepLink }) {
  const [busy, setBusy] = useState(false);
  const c = challenge;
  const active = c.is_active_period;

  const call = useCallback(async (path) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/challenges/${c.slug}/${path}`, {}, { withCredentials: true });
      onUpdate(data);
    } catch (e) {
      console.warn('challenge action failed', e);
    } finally { setBusy(false); }
  }, [c.slug, onUpdate]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-2 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="challenge-detail-modal"
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
        style={{
          background: `linear-gradient(160deg, #0d1a1a 0%, #1a2a24 45%, #0d1a1a 100%)`,
          maxHeight: '95vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Ambient glow */}
        <div
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full opacity-40 pointer-events-none"
          style={{ background: `radial-gradient(circle, ${c.accent} 0%, transparent 60%)`, filter: 'blur(40px)' }}
        />

        {/* Header row */}
        <div className="relative z-10 flex items-start justify-between p-4">
          {active ? (
            <div className="px-3 py-1 rounded-full bg-white/95 text-[10px] font-black tracking-wider text-black">
              ÄGER RUM NU
            </div>
          ) : (
            <div className="px-3 py-1 rounded-full bg-white/10 text-[10px] font-black tracking-wider text-white/80">
              KOMMANDE
            </div>
          )}
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition"
            data-testid="challenge-detail-close"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>

        {/* Hero visual with progress ring */}
        <div className="relative z-10 h-[280px] flex flex-col items-center justify-center px-4">
          <div className="relative w-[240px] h-[240px] flex items-center justify-center">
            <ProgressRing percent={c.percent} size={240} stroke={4} color={c.accent} />
            <div className="flex flex-col items-center justify-center gap-2">
              <ChallengeIcon emoji={c.icon_emoji} size="lg" celebrating={c.is_completed} />
              <CalendarPlate startDate={c.start_date} endDate={c.end_date} isActive={active} accent={c.accent} />
            </div>
          </div>
        </div>

        {/* Text block */}
        <div className="relative z-10 px-6 pt-4 pb-2">
          <div className="text-[10px] tracking-widest font-black text-white/50">UTMANING</div>
          <div className="flex items-start justify-between gap-3 mt-1">
            <h2 className="text-white text-2xl font-black leading-tight" style={{ fontFamily: '"Fredoka", sans-serif' }}>
              {c.title}
            </h2>
            <button className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0" title="Dela">
              <Share2 className="w-4 h-4 text-white/70" />
            </button>
          </div>
          <p className="text-white/70 text-sm leading-relaxed mt-2">{c.description}</p>
          {c.today_part && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-black text-black" style={{ background: c.accent }} data-testid="challenge-today-part">
              Dag {c.day_number} · Idag: {c.today_part.emoji} {c.today_part.name}
            </div>
          )}

          {c.joined && (
            <div className="mt-4 flex items-center gap-3 text-xs">
              <div className="flex-1">
                <div className="text-white/50 tracking-wide">FRAMSTEG</div>
                <div className="text-white font-black text-lg">{c.completed_count} / {c.duration_days} dagar</div>
              </div>
              <div className="text-right">
                <div className="text-white/50 tracking-wide">KVAR</div>
                <div className="text-white font-black text-lg">{c.days_left} d</div>
              </div>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="relative z-10 px-4 pb-4 pt-2 border-t border-white/10 mt-3">
          {!c.joined && (
            <button
              onClick={() => call('join')}
              disabled={busy || !active}
              className="w-full py-3 rounded-full bg-white text-black font-black text-sm hover:scale-[1.02] transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="challenge-join-btn"
            >
              {active ? '🚀  Delta i utmaningen' : 'Utmaningen har inte börjat än'}
            </button>
          )}
          {c.joined && !c.is_completed && (
            <div className="flex gap-2">
              <button
                onClick={() => call(c.today_checked ? 'uncheck' : 'checkin')}
                disabled={busy}
                className={`flex-1 py-3 rounded-full font-black text-sm transition ${
                  c.today_checked
                    ? 'bg-[#4ADE80] text-black'
                    : 'bg-white text-black hover:scale-[1.02]'
                }`}
                data-testid="challenge-checkin-btn"
              >
                {c.today_checked ? <><Check className="inline w-4 h-4 mr-1" strokeWidth={3} />  Klart idag</> : '✅  Check-in idag'}
              </button>
              <button
                onClick={() => { onDeepLink(c.deep_link_tab); onClose(); }}
                className="px-4 py-3 rounded-full bg-white/10 text-white font-black text-sm hover:bg-white/20 transition"
                data-testid="challenge-open-tab-btn"
              >
                Öppna
              </button>
            </div>
          )}
          {c.is_completed && (
            <div className="text-center py-2">
              <div className="text-3xl mb-1">🎊🐹🎊</div>
              <div className="text-white font-black text-base">Grattis — du klarade det!</div>
              <div className="text-white/60 text-xs mt-1">Cappy strålar av stolthet</div>
            </div>
          )}
          <div className="text-center text-white/40 text-[10px] mt-3 italic">
            {c.hero_line}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes confetti {
          0% { opacity: 0; transform: translate(-50%,-50%) scale(0.5) rotate(0deg); }
          20% { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%, calc(-50% + 40px)) scale(1) rotate(180deg); }
        }
      `}</style>
    </div>
  );
}

// ============== CARD (list item) ==============
function ChallengeCard({ challenge, onOpen }) {
  const c = challenge;
  return (
    <button
      onClick={() => onOpen(c)}
      className="group relative w-full text-left rounded-2xl overflow-hidden transition hover:scale-[1.01]"
      style={{
        background: 'linear-gradient(160deg, #0d1a1a 0%, #1a2a24 60%, #0d1a1a 100%)',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      }}
      data-testid={`challenge-card-${c.slug}`}
    >
      <div
        className="absolute -top-12 right-0 w-[220px] h-[220px] rounded-full opacity-30 pointer-events-none group-hover:opacity-50 transition"
        style={{ background: `radial-gradient(circle, ${c.accent} 0%, transparent 60%)`, filter: 'blur(30px)' }}
      />
      <div className="relative z-10 p-4 flex gap-4 items-center min-h-[110px]">
        <div className="flex-shrink-0 relative">
          <ChallengeIcon emoji={c.icon_emoji} size="md" celebrating={c.is_completed} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            {c.is_active_period ? (
              <span className="px-2 py-0.5 rounded-full bg-white text-black text-[8px] font-black tracking-wider">ÄGER RUM NU</span>
            ) : (
              <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/70 text-[8px] font-black tracking-wider">KOMMANDE</span>
            )}
            {c.joined && !c.is_completed && (
              <span className="px-2 py-0.5 rounded-full bg-[#4ADE80]/20 text-[#4ADE80] text-[8px] font-black tracking-wider">DELTAR</span>
            )}
            {c.is_completed && (
              <span className="px-2 py-0.5 rounded-full bg-[#FFD700]/20 text-[#FFD700] text-[8px] font-black tracking-wider">KLAR ✨</span>
            )}
          </div>
          <div className="text-[10px] tracking-widest text-white/40 font-black">UTMANING</div>
          <div className="text-white text-base font-black leading-tight" style={{ fontFamily: '"Fredoka", sans-serif' }}>
            {c.title}
          </div>
          <div className="text-white/50 text-[11px] mt-1 line-clamp-2">{c.hero_line}</div>
          {c.today_part && c.joined && (
            <div className="text-[11px] font-black mt-1" style={{ color: c.accent }}>Idag: {c.today_part.emoji} {c.today_part.name}</div>
          )}
          {c.joined && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${c.percent}%`, background: c.accent }}
                />
              </div>
              <span className="text-white/60 text-[10px] font-black tabular-nums">{c.completed_count}/{c.duration_days}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// ============== MAIN HUB ==============
export default function ChallengesHub() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/challenges/list`, { withCredentials: true });
      setChallenges(data.challenges || []);
    } catch (e) {
      console.warn('load challenges failed', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = (c) => setSelected(c);
  const closeDetail = () => setSelected(null);
  const handleUpdate = (updated) => {
    setSelected(updated);
    setChallenges((prev) => prev.map((x) => x.slug === updated.slug ? updated : x));
  };
  const handleDeepLink = (tab) => {
    if (tab === 'minnes') { try { sessionStorage.setItem('minnes-subtab', 'hp'); } catch {} }
    try { window.dispatchEvent(new CustomEvent('focus-critter:navigate', { detail: { tab } })); } catch {}
  };

  return (
    <div className="min-h-[400px]" data-testid="challenges-hub">
      <div className="max-w-2xl mx-auto p-3">
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C8B6FF]" />
            <h1 className="text-2xl font-black text-[#4E4370]" style={{ fontFamily: '"Fredoka", sans-serif' }}>
              Utmaningar
            </h1>
          </div>
          <p className="text-sm text-[#7D6BA0] mt-1 italic">
            Små satsningar som blir stora — Cappy hejar på från sidan 🐾
          </p>
        </div>

        {loading && (
          <div className="text-center text-[#7D6BA0] py-10 text-sm">Hämtar utmaningar…</div>
        )}

        {!loading && challenges.length === 0 && (
          <div className="text-center text-[#7D6BA0] py-10 text-sm">Inga utmaningar just nu</div>
        )}

        <div className="space-y-3">
          {challenges.map((c) => (
            <ChallengeCard key={c.slug} challenge={c} onOpen={openDetail} />
          ))}
        </div>
      </div>

      {selected && (
        <ChallengeDetailModal
          challenge={selected}
          onClose={closeDetail}
          onUpdate={handleUpdate}
          onDeepLink={handleDeepLink}
        />
      )}
    </div>
  );
}
